/**
 * SyncService - Handles synchronizing offline data to the server.
 *
 * Called when the user manually taps the "Sync" button (NOT auto-sync).
 * Processes each pending store in order:
 *	 1. Work orders	 -> POST /work-orders
 *	 2. Inspections	 -> POST /inspections
 *	 3. Time logs	 -> POST /work-orders/{id}/time-logs
 *	 4. Photos		 -> POST /work-orders/{id}/photos or /inspections/{id}/photos
 *	 5. Updates		 -> PATCH /work-orders/{id} or /inspections/{id}
 *
 * Returns a sync report with successes/failures so the UI can display
 * a summary to the user.
 *
 * Each individual item is synced independently — a failure on one item
 * does not prevent the remaining items from being attempted. Failed items
 * remain in IndexedDB for retry on the next sync.
 */

import { api } from './api';
import {
	getPendingItems,
	removePendingItem,
	STORE_NAMES,
} from './offlineStorage';
import { uploadService } from './uploadService';
import { photoTypeService } from './photoTypeService';
import { dataUrlToFile } from '../utils/imageCompression';

/**
 * SyncReport - Summary of a sync operation.
 *
 * @property synced - Number of items that were successfully sent to the server
 * @property failed - Number of items that failed to sync (still in IndexedDB)
 * @property errors - Human-readable error messages for each failure
 */
export interface SyncReport {
	synced: number;
	failed: number;
	errors: string[];
}

/**
 * syncAll() - Syncs all pending offline data to the server.
 *
 * Processes stores in a specific order because later items may depend
 * on earlier ones (e.g., photos reference work order IDs that must exist
 * on the server first).
 *
 * Order: work orders -> inspections -> time logs -> photos -> updates
 *
 * @returns A SyncReport summarizing successes, failures, and error messages
 */
export async function syncAll(): Promise<SyncReport> {
	// Aggregate results from all five sync operations
	const report: SyncReport = { synced: 0, failed: 0, errors: [] };

	// Process each store in dependency order
	const workOrderResult = await syncWorkOrders();
	const inspectionResult = await syncInspections();
	const timeLogResult = await syncTimeLogs();
	const photoResult = await syncPhotos();
	const updateResult = await syncUpdates();
	// Checklist updates (toggles, verify, sign-off) are synced last so the
	// underlying work orders/photos are guaranteed to exist server-side first.
	const checklistResult = await syncChecklistUpdates();

	// Merge all individual reports into the aggregate
	const results = [workOrderResult, inspectionResult, timeLogResult, photoResult, updateResult, checklistResult];
	for (const result of results) {
		report.synced += result.synced;
		report.failed += result.failed;
		report.errors.push(...result.errors);
	}

	return report;
}

/**
 * syncWorkOrders() - Syncs pending work orders to the server.
 *
 * Each work order is POSTed to /work-orders. On success the local
 * IndexedDB record is removed. On failure the record stays for retry.
 *
 * @returns A SyncReport for just the work order sync
 */
export async function syncWorkOrders(): Promise<SyncReport> {
	const report: SyncReport = { synced: 0, failed: 0, errors: [] };

	// Fetch all pending work orders from IndexedDB
	const items = await getPendingItems(STORE_NAMES.WORK_ORDERS);

	for (const item of items) {
		try {
			// POST the work order payload to the server
			await api.post('/work-orders', item.data);

			// Success — remove from local storage so it won't be re-sent
			if (item.id !== undefined) {
				await removePendingItem(STORE_NAMES.WORK_ORDERS, item.id);
			}
			report.synced++;
		} catch (error) {
			// Failed — leave in IndexedDB for retry, log the error
			report.failed++;
			report.errors.push(
				`Work order sync failed: ${error instanceof Error ? error.message : 'Unknown error'}`
			);
		}
	}

	return report;
}

/**
 * syncInspections() - Syncs pending inspections to the server.
 *
 * Each inspection is POSTed to /inspections. Same success/failure
 * handling as syncWorkOrders.
 *
 * @returns A SyncReport for just the inspection sync
 */
export async function syncInspections(): Promise<SyncReport> {
	const report: SyncReport = { synced: 0, failed: 0, errors: [] };

	// Fetch all pending inspections from IndexedDB
	const items = await getPendingItems(STORE_NAMES.INSPECTIONS);

	for (const item of items) {
		try {
			// POST the inspection payload to the server
			await api.post('/inspections', item.data);

			// Success — remove from local storage
			if (item.id !== undefined) {
				await removePendingItem(STORE_NAMES.INSPECTIONS, item.id);
			}
			report.synced++;
		} catch (error) {
			// Failed — leave for retry
			report.failed++;
			report.errors.push(
				`Inspection sync failed: ${error instanceof Error ? error.message : 'Unknown error'}`
			);
		}
	}

	return report;
}

/**
 * syncTimeLogs() - Syncs pending time log entries to the server.
 *
 * Each time log is POSTed to /work-orders/{workOrderId}/time-logs.
 * The work order ID is expected to be in item.data.workOrderId.
 *
 * @returns A SyncReport for just the time log sync
 */
export async function syncTimeLogs(): Promise<SyncReport> {
	const report: SyncReport = { synced: 0, failed: 0, errors: [] };

	// Fetch all pending time logs from IndexedDB
	const items = await getPendingItems(STORE_NAMES.TIME_LOGS);

	for (const item of items) {
		try {
			// Extract the parent work order ID from the stored data
			const workOrderId = item.data.workOrderId;
			if (!workOrderId) {
				throw new Error('Time log missing workOrderId');
			}

			// POST to the nested time-logs endpoint
			await api.post(`/work-orders/${workOrderId}/time-logs`, item.data);

			// Success — remove from local storage
			if (item.id !== undefined) {
				await removePendingItem(STORE_NAMES.TIME_LOGS, item.id);
			}
			report.synced++;
		} catch (error) {
			// Failed — leave for retry
			report.failed++;
			report.errors.push(
				`Time log sync failed: ${error instanceof Error ? error.message : 'Unknown error'}`
			);
		}
	}

	return report;
}

/**
 * syncPhotos() - Syncs photos captured offline to the server.
 *
 * Photos are queued by the capture surfaces (WorkOrderDetailPage /
 * InspectionDetailPage) when the device has no connection. Each stored item
 * carries:
 *	 - item.data.parentType:    'work-order' | 'inspection'
 *	 - item.data.parentId:      id of the parent entity
 *	 - item.data.base64:        the compressed photo as a data URL
 *	 - item.data.fileName:      original file name
 *	 - item.data.mimeType:      MIME type of the compressed image
 *	 - item.data.companyId:     company that owns the parent, for type lookup
 *	 - item.data.photoTypeName: seeded photo type NAME ('Before', 'Observation', …)
 *	 - item.data.caption:       optional caption text
 *
 * Two things are deliberately deferred from capture time to here, because
 * both need a network the device didn't have:
 *
 *	 1. The photo_type_id lookup. Offline we only know the seeded NAME, so the
 *	    id is resolved now, against the company that owns the parent record.
 *	 2. The multipart encoding. The photos endpoints take either a multipart
 *	    `file` or a JSON `photo_url` — a base64 blob in the JSON body is not a
 *	    shape either branch accepts, so the data URL is rehydrated into a File
 *	    and sent the same way an online capture would send it.
 *
 * @returns A SyncReport for just the photo sync
 */
export async function syncPhotos(): Promise<SyncReport> {
	const report: SyncReport = { synced: 0, failed: 0, errors: [] };

	// Fetch all pending photos from IndexedDB
	const items = await getPendingItems(STORE_NAMES.PHOTOS);

	for (const item of items) {
		try {
			const parentType = item.data.parentType as string;
			const parentId = item.data.parentId;
			const base64 = item.data.base64 as string | undefined;

			if (!parentType || !parentId) {
				throw new Error('Photo missing parentType or parentId');
			}
			if (!base64) {
				throw new Error('Photo missing image data');
			}

			// Resolve the seeded type name to this company's photo_types row.
			const companyId = item.data.companyId as string | null | undefined;
			const photoTypeName = (item.data.photoTypeName as string | undefined) ?? 'Observation';
			const photoTypeId = companyId
				? await photoTypeService.resolveByName(String(companyId), photoTypeName)
				: null;

			if (!photoTypeId) {
				throw new Error(`No "${photoTypeName}" photo type is configured for this company`);
			}

			// Rebuild the File the capture surface would have uploaded.
			const fileName = (item.data.fileName as string | undefined) ?? 'photo.jpg';
			const mimeType = (item.data.mimeType as string | undefined) ?? 'image/jpeg';
			const file = await dataUrlToFile(base64, fileName, mimeType);

			const metadata = {
				photoTypeId,
				caption: (item.data.caption as string | undefined) ?? undefined,
			};

			if (parentType === 'work-order') {
				await uploadService.uploadWorkOrderPhoto(String(parentId), file, metadata);
			} else {
				await uploadService.uploadInspectionPhoto(String(parentId), file, metadata);
			}

			// Success — remove from local storage
			if (item.id !== undefined) {
				await removePendingItem(STORE_NAMES.PHOTOS, item.id);
			}
			report.synced++;
		} catch (error) {
			// Failed — leave in IndexedDB for the next sync attempt
			report.failed++;
			report.errors.push(
				`Photo sync failed: ${error instanceof Error ? error.message : 'Unknown error'}`
			);
		}
	}

	return report;
}

/**
 * syncUpdates() - Syncs pending status updates, notes, and other patches.
 *
 * Updates are PATCH requests to existing entities. The target is
 * determined by item.data:
 *	 - item.data.entityType: 'work-order' | 'inspection'
 *	 - item.data.entityId: numeric ID of the entity to update
 *	 - item.data.payload: the partial update fields
 *
 * The endpoint is:
 *	 - 'work-order'	 -> PATCH /work-orders/{entityId}
 *	 - 'inspection'	 -> PATCH /inspections/{entityId}
 *
 * @returns A SyncReport for just the update sync
 */
/**
 * syncChecklistUpdates() - Syncs pending checklist toggles, verifications,
 * and sign-offs that were queued while offline (or after a transient API
 * failure during an online toggle). Each pending item carries a `type`
 * discriminator that maps to one of three server endpoints:
 *
 *	 - 'checklist_toggle' -> POST /checklist-items/{itemId}/toggle
 *	 - 'checklist_verify' -> POST /work-orders/{workOrderId}/checklist/verify
 *	 - 'checklist_submit' -> POST /work-orders/{workOrderId}/checklist/submit
 *
 * Without this, items written by useWorkOrderWorkflow into the
 * CHECKLIST_UPDATES store would never drain — the "Sync Now" banner
 * would stay visible indefinitely with no API calls being made.
 *
 * @returns A SyncReport for just the checklist update sync
 */
export async function syncChecklistUpdates(): Promise<SyncReport> {
	const report: SyncReport = { synced: 0, failed: 0, errors: [] };

	// Fetch all pending checklist updates from IndexedDB
	const items = await getPendingItems(STORE_NAMES.CHECKLIST_UPDATES);

	for (const item of items) {
		try {
			// Each queued update tags itself with a type discriminator so
			// we know which endpoint shape to use
			const type = item.data.type as string;
			const workOrderId = item.data.workOrderId as string;

			if (!type || !workOrderId) {
				throw new Error('Checklist update missing type or workOrderId');
			}

			if (type === 'checklist_toggle') {
				// Single item completion toggle
				const itemId = item.data.itemId as string;
				const isCompleted = !!item.data.isCompleted;
				if (!itemId) {
					throw new Error('Checklist toggle missing itemId');
				}
				await api.post(`/checklist-items/${itemId}/toggle`, { isCompleted });
			} else if (type === 'checklist_verify') {
				// Verification step with optional photos
				const photos = (item.data.photos as { base64: string; caption?: string }[]) ?? [];
				await api.post(`/work-orders/${workOrderId}/checklist/verify`, { photos });
			} else if (type === 'checklist_submit') {
				// Final sign-off with signature
				const signatureDataUrl = item.data.signatureDataUrl as string;
				const notes = item.data.notes as string | undefined;
				if (!signatureDataUrl) {
					throw new Error('Checklist submit missing signatureDataUrl');
				}
				await api.post(`/work-orders/${workOrderId}/checklist/submit`, {
					signatureDataUrl,
					notes,
				});
			} else {
				throw new Error(`Unknown checklist update type: ${type}`);
			}

			// Success — remove from local storage
			if (item.id !== undefined) {
				await removePendingItem(STORE_NAMES.CHECKLIST_UPDATES, item.id);
			}
			report.synced++;
		} catch (error) {
			// Failed — leave for retry on next sync
			report.failed++;
			report.errors.push(
				`Checklist update sync failed: ${error instanceof Error ? error.message : 'Unknown error'}`
			);
		}
	}

	return report;
}

export async function syncUpdates(): Promise<SyncReport> {
	const report: SyncReport = { synced: 0, failed: 0, errors: [] };

	// Fetch all pending updates from IndexedDB
	const items = await getPendingItems(STORE_NAMES.UPDATES);

	for (const item of items) {
		try {
			// Determine the correct endpoint based on entity type
			const entityType = item.data.entityType as string;
			const entityId = item.data.entityId;
			const payload = item.data.payload;

			if (!entityType || !entityId) {
				throw new Error('Update missing entityType or entityId');
			}

			// Build the endpoint URL based on entity type
			const endpoint = entityType === 'work-order'
				? `/work-orders/${entityId}`
				: `/inspections/${entityId}`;

			// PATCH the entity with the partial update payload
			await api.patch(endpoint, payload);

			// Success — remove from local storage
			if (item.id !== undefined) {
				await removePendingItem(STORE_NAMES.UPDATES, item.id);
			}
			report.synced++;
		} catch (error) {
			// Failed — leave for retry
			report.failed++;
			report.errors.push(
				`Update sync failed: ${error instanceof Error ? error.message : 'Unknown error'}`
			);
		}
	}

	return report;
}
